import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import App from './App.vue'
import router from './router'
import { i18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { configureAsyncData } from '@lib/composables/useAsyncData'
import { is401Error } from '@/lib/errors'
import { primevueOptions, csLocale, enLocale } from '@/config/primevue'
import 'primeicons/primeicons.css'
import './styles/main.css'

/*
 * Apply persisted theme to <html> BEFORE Vue mounts to avoid a flash
 * of light mode on cold load. The Pinia ui store reads the same
 * localStorage key (`bzuco_theme`) and re-applies on hydration.
 */
;(() => {
    try {
        const stored = localStorage.getItem('bzuco_theme') || 'auto'
        const resolved =
            stored === 'auto'
                ? window.matchMedia('(prefers-color-scheme: dark)').matches
                    ? 'dark'
                    : 'light'
                : stored === 'dark'
                  ? 'dark'
                  : 'light'
        document.documentElement.setAttribute('data-theme', resolved)
    } catch {
        // ignore — SSR / restricted environments
    }
})()

const app = createApp(App)
app.use(createPinia())
app.use(router)
app.use(i18n)
app.use(PrimeVue, primevueOptions)

watch(
    i18n.global.locale,
    newLoc => {
        const config = (
            app as unknown as {
                config: { globalProperties: { $primevue?: { config: { locale: unknown } } } }
            }
        ).config.globalProperties.$primevue
        if (config) {
            config.config.locale = newLoc === 'en' ? enLocale : csLocale
        }
    },
    { immediate: false }
)

// Inject app-specific 401 handler do _lib useAsyncData
configureAsyncData({
    onUnauthenticated: () => {
        const auth = useAuthStore()
        auth.logout()
        const next = router.currentRoute.value.fullPath
        router.push({ name: 'login', query: next !== '/' ? { next } : {} })
    },
    is401: is401Error
})

app.mount('#app')
